import { Pet, PetRehomingRequest } from './pet';

interface UserName {
  firstName: string;
  lastName: string;
}

export enum UserTelephoneType {
  Main = 1,
  Second,
}

export enum ProductType {
  Basic = 1,
  Premium,
  LegacyPremium,
  LegacyPlus,
  BasicPlus,
  Locator
}

export enum AccountType {
  Undefined,
  SuperAdmin,
  CustomerAdmin,
  WarehouseAdmin,
  Keeper,
  Carer,
  Vet,
  Breeder,
  Registrar,
  ReHomer,
  CRN,
}

export enum ErrorCode {
  InvalidCredentials = 5000,
  UnknownFailure = 5001,
  InvalidRole = 5002,
  MultiplePhoneNumbers = 5005,
  RegistrationNotAllowed = 5006
}

export interface UserTelephone {
  readonly id?: number;
  name?: string;
  number: string;
  numberType: UserTelephoneType;
}

export interface UserContact {
  email?: string;
  mainPhoneNumber: string;
  secondaryPhoneNumber: string;
  temporaryContactName?: string;
  temporaryPhoneNumber?: string;
}

export enum UserAddressType {
  Main = 1,
  HolidayHome,
  CarersHome,
  RelativesHome,
  HolidayDestination,
  CarersAddress,
  KennelOrLodgings,
  Other,
}

interface UserAddressTemporaryInfo {
  dateStart: Date;
  dateEnd: Date;
}

export interface BannerDetails {
  id: number;
  name: string,
  message: string,
  color: string,
  isEnabled: boolean
}

export interface UserAddress {
  readonly id?: number;
  line1: string;
  line2: string;
  town: string;
  county: string;
  postcode: string;
  addressType: UserAddressType;
  temporary: UserAddressTemporaryInfo;
  temporaryPhoneNumber?: string;
  temporaryContactName?: string;
}

export interface UserCommunicationPreferences {
  email: boolean;
  sms: boolean;
  telephone: boolean;
  thirdPartyCommunicationEmail: boolean;
  thirdPartyCommunicationSms: boolean;
  inverseThirdPartyCommunicationEmail: boolean;
  inverseThirdPartyCommunicationSms: boolean;
}

export interface CommunicationPreferencesSettings {
  emailChecked: boolean;
  smsChecked: boolean;
  telephoneChecked: boolean;
}

export enum KeeperResponseStatus {
  ConsentGivenResponse = 1,
  ConsentDeniedResponse = 2,
}

export interface User {
  readonly id: number;
  readonly token: string;
  readonly isActive: boolean;
  readonly stripeCustomerId: string;
  readonly stripePriceId: string;
  addresses: UserAddress[];
  communicationPreferences: UserCommunicationPreferences;
  contact: UserContact;
  name: UserName;
  readonly productType: ProductType;
  readonly originalProductType: ProductType;
  readonly accountType: AccountType;
  readonly roles: AccountType[];
  pets: Pet[];
  readonly detailsUpdatable: boolean;
  readonly ultimate: boolean;
  updatedAfterThirdPartyCommAdd: number;
  readonly title: string;
  readonly dateOfBirth: Date;
  readonly gender: string;
  readonly isSocialLoginUser: boolean;
  readonly hasOpenTokRequests: boolean;
  created: Date;
  tokRequests: TokRequest[];
  readonly registrationSource: RegistrationSource;
}

export enum RegistrationSource {
  Unknown = 0,
  SelfRegisteredWeb = 1,
  SelfRegisteredMobile = 2,
  SelfRegisteredB2C = 3,
  AdminCustomerAdmin = 4,
  AdminTeamLeader = 5,
  AdminVet = 6,
  AdminRescue = 7,
  AdminRescuePlus = 8,
  BreederTok = 9,
  AdminTok = 10,
  KeeperTok = 11,
  LegacyKeeperTok = 12,
  VetXmlFromVetEnvoy = 13,
  VetXmlV2 = 14,
  ApiKeyUser = 15,
  LegacyChipImport = 16,
  RescueTok = 17,
  VetTok = 18,
  HuntTok = 19,
  PmsImportAT = 20,
  PmsImportAMC = 21,
  PmsImportWTP = 22,
  PmsImportMCC = 23,
  AdminImplanter = 24,
  PremiumBreederTok = 25,
  PlusBreederTok = 26,
  VetXmlFromAT = 27,
}

export enum TokRequestType {
  KeeperTransfer = 1,
  BreederSale,
  Legacy,
  Foreign,
  ForeignBreederSale,
  FreeBreederSale,
  Rescue,
  BasePlusBreederSale
}

export interface TokRequest {
  chipNumber: string;
  id: number;
  newKeeperId: number;
  oldKeeperId: number;
  createdById: number;
  status: number;
  type: TokRequestType;
  pet: PetRehomingRequest;
}

export interface RegistrationPaymentContext {
  brand?: string;
  chipSupplier?: string;
  vetGroup?: string;
  b21k?: string;
}

export type PriceInterval = 'year' | 'month';

// One purchasable price for a product, as returned by /payment. amount is in minor units
// (for example 3900 = £39.00).
export interface ProductPrice {
  priceId: string;
  interval: PriceInterval;
  currency: string;
  amount: number;
}

// The products the backend allows, keyed by ProductType name (for example 'Premium', 'BasicPlus'),
// each mapped to the prices available for it (one per billing interval). The set of keys can grow
// as new products become purchasable through /payment.
export type AvailableProducts = { [productName: string]: ProductPrice[] };

export interface RegistrationPaymentInfo {
  availableProducts: AvailableProducts;
  // Configurable flow label set by the backend, for example 'Default', 'Skip to dashboard'.
  // Behaviour is driven by isPaymentSkippable and availableProducts, not by this name.
  name: string;
  isPaymentSkippable: boolean;
  context: RegistrationPaymentContext | null;
}

// GET /api/registration/me/steps returns the ordered step identifiers for this user's flow and the step
// the user is on. POST to the same endpoint with { step } completes a step, which moves currentStep to the
// step after it when the posted step is the same as or later than the current one.
export interface RegistrationStepsResponse {
  // See RegistrationFlowType.
  type: string;
  steps: string[];
  currentStep: string;
}
